import { IBaseAction } from '@models/actions/IBaseAction';

/**
 * «Быстрый доступ» меню «Создать документ»: закреплённые действия + добивка по
 * frecency (частота × свежесть с экспоненциальным затуханием, как адресная строка
 * Firefox). Состояние живёт в localStorage per-пользователь и ссылается на действия
 * по их конфиг-ключам. Действия со временем удаляются из конфига или скрываются
 * правами, поэтому отображение ВСЕГДА резолвится против текущего списка с сервера:
 * отсутствующий ключ не рендерится, но запись переживает временное скрытие —
 * вычищается только по возрасту.
 */

export interface IQuickUsageEntry {
    count: number;
    /** Момент последнего использования, Date.now(). */
    lastUsed: number;
}

export interface IQuickAccessState {
    /** Ключи закреплённых действий в порядке закрепления. */
    pinned: string[];
    usage: Record<string, IQuickUsageEntry>;
}

export const DEFAULT_QUICK_STATE: IQuickAccessState = { pinned: [], usage: {} };

export const QUICK_ACCESS_LIMIT = 6;
/** Служебный id секции — не пересекается с именами разделов из конфига. */
export const QUICK_SECTION_ID = '_quick';
/** Нейтральный оттенок секции: быстрый доступ — срез, а не раздел конфига. */
export const QUICK_SECTION_HUE = '#8c94a6';

const DAY_MS = 24 * 60 * 60 * 1000;
/** За две недели без использования вес записи падает вдвое. */
const HALF_LIFE_DAYS = 14;
/** Записи, не использованные полгода, вычищаются из хранилища. */
const PRUNE_AFTER_DAYS = 180;
/** Ниже этого веса запись не показывается (count=1 стухает за ~2 месяца). */
const MIN_SCORE = 0.05;

const isUsageEntry = (value: unknown): value is IQuickUsageEntry => {
    const entry = value as IQuickUsageEntry | null;
    return typeof entry?.count === 'number' && typeof entry?.lastUsed === 'number';
};

/** Хранилище пользовательское и переживает версии кода — читаем недоверчиво. */
export const sanitizeQuickState = (state: Partial<IQuickAccessState> | null | undefined): IQuickAccessState => {
    const pinned = Array.isArray(state?.pinned) ? state.pinned.filter((key) => typeof key === 'string') : [];
    const usage: Record<string, IQuickUsageEntry> = {};
    if (state?.usage && typeof state.usage === 'object') {
        Object.entries(state.usage).forEach(([key, entry]) => {
            if (isUsageEntry(entry)) {
                usage[key] = entry;
            }
        });
    }
    return { pinned, usage };
};

const frecencyScore = (entry: IQuickUsageEntry, now: number): number =>
    entry.count * Math.pow(0.5, Math.max(0, now - entry.lastUsed) / DAY_MS / HALF_LIFE_DAYS);

/**
 * Действия секции «Быстрый доступ»: закреплённые (в порядке закрепления), затем
 * по убыванию frecency, всего не больше QUICK_ACCESS_LIMIT. Ключи, отсутствующие
 * в текущем списке действий (удалены из конфига или скрыты правами), не попадают
 * в выдачу.
 */
export const selectQuickActions = (
    state: IQuickAccessState | null | undefined,
    actions: IBaseAction[],
    now: number,
): IBaseAction[] => {
    const safe = sanitizeQuickState(state);
    const byKey = new Map(actions.map((action) => [action.key, action]));

    const pinned = safe.pinned.map((key) => byKey.get(key)).filter((a): a is IBaseAction => Boolean(a));

    const auto = Object.entries(safe.usage)
        .filter(([key]) => !safe.pinned.includes(key) && byKey.has(key))
        .map(([key, entry]) => ({ action: byKey.get(key) as IBaseAction, score: frecencyScore(entry, now) }))
        .filter(({ score }) => score >= MIN_SCORE)
        .sort((a, b) => b.score - a.score)
        .map(({ action }) => action);

    return [...pinned, ...auto].slice(0, QUICK_ACCESS_LIMIT);
};

/** Регистрирует использование действия и попутно вычищает записи старше полугода. */
export const registerActionUse = (
    state: IQuickAccessState | null | undefined,
    key: string,
    now: number,
): IQuickAccessState => {
    const safe = sanitizeQuickState(state);
    const usage: Record<string, IQuickUsageEntry> = {};
    Object.entries(safe.usage).forEach(([usageKey, entry]) => {
        if (now - entry.lastUsed <= PRUNE_AFTER_DAYS * DAY_MS) {
            usage[usageKey] = entry;
        }
    });
    usage[key] = { count: (usage[key]?.count ?? 0) + 1, lastUsed: now };
    return { pinned: safe.pinned, usage };
};

export const togglePinned = (state: IQuickAccessState | null | undefined, key: string): IQuickAccessState => {
    const safe = sanitizeQuickState(state);
    return {
        ...safe,
        pinned: safe.pinned.includes(key) ? safe.pinned.filter((k) => k !== key) : [...safe.pinned, key],
    };
};
